import { cn } from '@invent/wl-ui-kit-next';
import { Alert, AlertDescription, AlertTitle, Button } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import type { RegistrationNumber } from '../types';
import { startFailure } from './choose-registration';
import { DOOR } from './registration-copy';
import { QuietAction, StepHeading } from './step-parts';

/** A tick on page 01: one of the numbers, or "None of the above". */
export type NumberTick = RegistrationNumber | 'none';

/** The ticks in the order page 01 draws them — and the run walks the numbers, CRD first. */
const TICKS: readonly NumberTick[] = ['crd', 'insurance', 'none'];

/**
 * Page 01, "Let's get you set up" — which numbers the registrant holds (SR-487 decision 3), the
 * screen before any run exists.
 *
 * **Three ticks, and "None of the above" stands alone**: ticking it unticks the numbers, and
 * ticking a number unticks it, so what Continue sends is always a set survey-api reads one way.
 * The numbers start the run (`{numbers}`, CRD first); "None" starts nothing yet — page 07 is
 * drawn first, and its Continue starts the run with no number. Continue waits for a tick: with
 * none, there is nothing to say yet that the wireframe has words for.
 *
 * Nothing else is on the screen — no way back to sign-in, as the wireframe has none.
 */
export function ChooseNumbers({
  ticks,
  onStart,
  onNone
}: {
  /** The ticks to draw on arrival — Back from an anchor or page 07 keeps what was chosen. */
  ticks?: readonly NumberTick[];
  onStart: (numbers: RegistrationNumber[]) => Promise<void>;
  onNone: () => void;
}) {
  const [ticked, setTicked] = React.useState<ReadonlySet<NumberTick>>(() => new Set(ticks));
  const { starting, failure, begin } = useStart();

  const toggle = (tick: NumberTick) =>
    setTicked((current) => {
      const next = new Set(current);
      if (next.has(tick)) {
        next.delete(tick);
        return next;
      }
      if (tick === 'none') return new Set(['none']);
      next.delete('none');
      next.add(tick);
      return next;
    });

  const go = async () => {
    if (ticked.has('none')) {
      onNone();
      return;
    }
    await begin(() =>
      onStart(
        TICKS.filter((tick): tick is RegistrationNumber => tick !== 'none' && ticked.has(tick))
      )
    );
  };

  return (
    <>
      <StepHeading title={DOOR.choose.title} lede={DOOR.choose.lede} />

      <div className="grid gap-3">
        {TICKS.map((tick) => (
          <TickBox
            key={tick}
            tick={tick}
            checked={ticked.has(tick)}
            disabled={starting}
            onToggle={() => toggle(tick)}
          />
        ))}
      </div>

      {failure && (
        <p role="alert" className="mt-4 text-sm font-medium text-destructive">
          {failure}
        </p>
      )}

      <Button
        className="mt-5 w-full"
        disabled={starting || ticked.size === 0}
        onClick={() => void go()}
      >
        {starting ? DOOR.checking : DOOR.continue}
      </Button>
    </>
  );
}

/** One tick, its box drawn round the label and hint as the wireframe draws it. Named by the
 *  label and described by the hint, as the survey chooser's choices were: the label is the
 *  answer, the hint advice. */
function TickBox({
  tick,
  checked,
  disabled,
  onToggle
}: {
  tick: NumberTick;
  checked: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  const words = DOOR.choose[tick];
  const id = `registration-tick-${tick}`;
  return (
    <label
      htmlFor={id}
      className={cn(
        'flex cursor-pointer items-start gap-3 rounded-md border px-4 py-3 transition-colors',
        checked
          ? 'border-[var(--color-sapphire)] bg-[var(--color-slight-blue)]/30'
          : 'border-border bg-white hover:border-[var(--color-ocean-blue)]'
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onToggle}
        aria-labelledby={`${id}-label`}
        aria-describedby={`${id}-hint`}
        className="mt-0.5 size-4 shrink-0 accent-[var(--color-sapphire)]"
      />
      <span className="text-sm">
        <span id={`${id}-label`} className="block font-medium text-[var(--color-royal-blue)]">
          {words.label}
        </span>
        <span id={`${id}-hint`} className="mt-0.5 block text-xs text-muted-foreground">
          {words.hint}
        </span>
      </span>
    </label>
  );
}

/**
 * Page 07, "That's fine" — "None of the above", before anything starts (decision 3). Continue
 * starts the run with no number, which goes straight to the account; Back returns to page 01
 * with nothing started, "None" still ticked.
 */
export function NoNumber({
  onContinue,
  onBack
}: {
  onContinue: () => Promise<void>;
  onBack: () => void;
}) {
  const { starting, failure, begin } = useStart();

  return (
    <>
      <StepHeading title={DOOR.none.title} lede={DOOR.none.lede} />
      <Alert className="mb-5">
        <AlertTitle>{DOOR.none.noticeTitle}</AlertTitle>
        <AlertDescription>{DOOR.none.notice}</AlertDescription>
      </Alert>

      {failure && (
        <p role="alert" className="mb-4 text-sm font-medium text-destructive">
          {failure}
        </p>
      )}

      <div className="grid">
        <Button disabled={starting} onClick={() => void begin(onContinue)}>
          {starting ? DOOR.checking : DOOR.continue}
        </Button>
        <QuietAction label={DOOR.back} disabled={starting} onClick={onBack} />
      </div>
    </>
  );
}

/** Both pages' Continue, which starts a run: `starting` from the press on — never cleared on
 *  success, as the page is replaced — and a start that failed said in `startFailure`'s words, the
 *  page pressable again. */
function useStart() {
  const [starting, setStarting] = React.useState(false);
  const [failure, setFailure] = React.useState<React.ReactNode>(null);

  const begin = async (start: () => Promise<void>) => {
    setStarting(true);
    setFailure(null);
    try {
      await start();
    } catch (error) {
      setFailure(startFailure(error));
      setStarting(false);
    }
  };

  return { starting, failure, begin };
}
